import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import {
  DFMCheck,
  DFMCheckType,
  DFMCheckStatus,
} from '../../../models/dfm-check.model';
import {
  DFM_CHECK_DEFINITIONS,
  DFM_STATUS_INFO,
  DFMStatusSeverity,
} from '../../../models/dfm-check-definitions';
import { Tag } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { DownloadService } from '../../services/download.service';

@Component({
  selector: 'app-dfm-check-item',
  imports: [Tag, ButtonModule],
  templateUrl: './dfm-check-item.component.html',
})
export class DfmCheckItemComponent {
  private downloadService = inject(DownloadService);

  @Input() dfmCheck: DFMCheck | null = null;
  @Input() selectedDFMCheck: DFMCheck | null = null;
  @Input() cadObjectName: string = '';
  @Output() toggleDFMCheck = new EventEmitter<DFMCheck>();

  checkTypeIcon(checkType: DFMCheckType): string {
    return DFM_CHECK_DEFINITIONS[checkType]?.icon || 'pi pi-info-circle';
  }

  checkTypeName(checkType: DFMCheckType): string {
    return DFM_CHECK_DEFINITIONS[checkType]?.label || checkType;
  }

  statusDisplay(status: DFMCheckStatus): string {
    return DFM_STATUS_INFO[status]?.label || status;
  }

  statusSeverity(status: DFMCheckStatus): DFMStatusSeverity {
    return DFM_STATUS_INFO[status]?.severity || 'info';
  }

  quantityToFloat(quantity: string | null): number {
    return parseFloat(quantity || '0');
  }

  formatQuantity(dfmCheck: DFMCheck): string {
    const definition = DFM_CHECK_DEFINITIONS[dfmCheck.check_type];
    const quantity = parseFloat(dfmCheck.quantity || '0');
    return definition.formatQuantity(dfmCheck, quantity);
  }

  isWarningType(checkType: DFMCheckType): boolean {
    return DFM_CHECK_DEFINITIONS[checkType]?.isWarningType ?? false;
  }

  get checkDescription(): string | null {
    return this.dfmCheck
      ? (DFM_CHECK_DEFINITIONS[this.dfmCheck.check_type].description ?? null)
      : null;
  }

  get checkParams(): Array<{ label: string; value: number }> {
    const { dfmCheck } = this;
    if (!dfmCheck?.dfm_check_parameters) return [];
    const labels = DFM_CHECK_DEFINITIONS[dfmCheck.check_type].parameterLabels;
    return Object.entries(dfmCheck.dfm_check_parameters).map(
      ([key, value]) => ({
        label: labels[key] ?? key,
        value,
      }),
    );
  }

  onToggleDFMCheck() {
    if (!this.dfmCheck) {
      return;
    }
    this.toggleDFMCheck.emit(this.dfmCheck);
  }

  get showBlockingUpStepDownload(): boolean {
    const c = this.dfmCheck;
    return (
      !!c &&
      c.check_type === DFMCheckType.BLOCKING_UP &&
      c.status === DFMCheckStatus.COMPLETED &&
      !!c.blocking_up_step_file_url
    );
  }

  async downloadBlockingUpStep(): Promise<void> {
    const url = this.dfmCheck?.blocking_up_step_file_url;
    if (!url) {
      return;
    }
    const filename = this.cadObjectName
      ? `${this.cadObjectName}-blocking-up.step`
      : 'blocking-up.step';
    await this.downloadService.downloadFile(url, {
      defaultFilename: filename,
      mimeType: 'application/octet-stream',
    });
  }
}
